import {
  applicantSection,
  feeInfo,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * A4. Wniosek o ustalenie numeru porządkowego — art. 47a ust. 6 PGiK; wzór: załącznik nr 2 do rozporządzenia MRPiT
 * z 21.07.2021 r. w sprawie ewidencji miejscowości, ulic i adresów (Dz.U. 2021 poz. 1368). Pola jak we wzorze
 * (wnioskodawca, miejscowość, działka ewidencyjna, obręb, budynek/budynki + przypis 2 o mapie lub szkicu).
 */
const MAP_REQUIRED: EbiCondition = {
  any: [
    { field: "liczbaBudynkow", equals: "wiele" },
    { field: "sytuacjaNiejednoznaczna", equals: true },
  ],
};

const A4: IEbiFormDefinition = {
  code: "A4",
  version: 1,
  title: "Wniosek o ustalenie numeru porządkowego",
  category: EEbiFormCategory.Property,
  office: EEbiFormOffice.Municipality,
  description: "Numer porządkowy (adres) dla budynku istniejącego albo w budowie.",
  keywords: ["numer porządkowy", "adres", "numeracja", "budynek", "dom"],
  organ: "Wójt / Burmistrz / Prezydent Miasta",
  legalBasis:
    "art. 47a ust. 6 ustawy z dnia 17 maja 1989 r. — Prawo geodezyjne i kartograficzne; rozporządzenie Ministra Rozwoju, Pracy i Technologii z dnia 21 lipca 2021 r. w sprawie ewidencji miejscowości, ulic i adresów (wzór — załącznik nr 2)",
  request: "Wnoszę o ustalenie numeru porządkowego budynkowi (budynkom) zlokalizowanemu na nieruchomości wskazanej poniżej.",
  feeLabel: "bez opłaty",
  fee: "Ustalenie numeru jest bezpłatne. Zaświadczenie o nadanym numerze — 17 zł.",
  deadlineLabel: "do 30 dni",
  deadline: "Zawiadomienie o ustaleniu numeru — do 30 dni.",
  sections: [
    applicantSection(),
    representationSection(),
    {
      code: "budynek",
      title: "Lokalizacja budynku",
      fields: [
        {
          code: "liczbaBudynkow",
          type: "radio",
          label: "Wniosek dotyczy",
          required: true,
          defaultValue: "jeden",
          options: [
            { value: "jeden", label: "jednego budynku" },
            { value: "wiele", label: "więcej niż jednego budynku" },
          ],
        },
        {
          code: "stanBudynku",
          type: "radio",
          label: "Budynek",
          required: true,
          options: [
            { value: "istniejacy", label: "istniejący" },
            { value: "wBudowie", label: "w budowie" },
          ],
        },
        { code: "miejscowoscBudynku", type: "text", label: "W miejscowości", required: true, maxLength: 100 },
        { code: "dzialka", type: "text", label: "Na działce ewidencyjnej", required: true, width: "half", maxLength: 100, placeholder: "np. 125/4" },
        { code: "obreb", type: "text", label: "W obrębie", required: true, width: "half", maxLength: 100 },
        {
          code: "sytuacjaNiejednoznaczna",
          type: "checkbox",
          label: "Sytuacja w terenie nie wskazuje jednoznacznie, któremu budynkowi ma zostać ustalony numer",
        },
        {
          code: "budynekPozaEwidencja",
          type: "checkbox",
          label: "Budynek nie figuruje jeszcze w ewidencji gruntów i budynków (np. nowo wybudowany)",
        },
      ],
    },
    {
      code: "zaswiadczenieSekcja",
      title: "Zaświadczenie",
      fields: [
        {
          code: "zaswiadczenie",
          type: "checkbox",
          label: "Proszę także o wydanie zaświadczenia o ustalonym numerze porządkowym (opłata skarbowa 17 zł)",
        },
        feeInfo("infoOplata", "Ustalenie numeru porządkowego jest bezpłatne; wynikiem jest zawiadomienie o ustaleniu numeru."),
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "mapa",
      label: "Mapa lub szkic z lokalizacją i oznaczeniem budynków",
      hint: "Wymagane, gdy wniosek dotyczy więcej niż jednego budynku albo sytuacja w terenie jest niejednoznaczna (przypis 2 wzoru).",
      requiredWhen: MAP_REQUIRED,
    },
    {
      code: "zakonczenieBudowy",
      label: "Zawiadomienie o zakończeniu budowy, pozwolenie na użytkowanie albo mapa z inwentaryzacji powykonawczej",
      hint: "Przyspiesza sprawę, gdy budynku nie ma jeszcze w ewidencji (praktyka urzędów).",
      visibleWhen: { field: "budynekPozaEwidencja", equals: true },
    },
    {
      code: "oplataZaswiadczenie",
      label: "Dowód zapłaty opłaty skarbowej za zaświadczenie (17 zł)",
      visibleWhen: { field: "zaswiadczenie", equals: true },
      required: true,
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default A4;
